// @flow

import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { includes, indexOf, last, range, reverse, sample, without } from 'lodash';
import Tile, { EmptyTile, newTile } from './Tile';
import Tiles from './Tiles';
import audioPlaylist from '../utils/audio_playlist';
import useWindowSize from '../utils/use_window_size';
import useGameState from '../utils/use_game_state';
import Game from '../Shared/Game';
import type { AudioAssets } from './types';

type Props = {
  assetPath: string,
  tileImages: Array<string>,
  audio: AudioAssets,
};

const levels = [
  { image: 'BG Level 1.png',
    origin: { top: (171 - 16), left: (178 - 16) },
    // Nodes in a path have an origin (relative steps right&down from levelOrigin) and a
    // direction they're facing
    path: [
      { origin: [1, 0], direction: 'east' },
      { origin: [2, 1], direction: 'south' },
      { origin: [3, 2], direction: 'east' },
    ] },

  { image: 'BG Level 2.png',
    origin: { top: (171 - 20), left: (298 - 16) },
    path: [
      { origin: [-1, 1], direction: 'west' },
      { origin: [-1, 2], direction: 'south' },
      { origin: [0, 3], direction: 'east' },
      { origin: [2, 3], direction: 'east' },
    ] },

  { image: 'BG Level 3.png',
    origin: { top: (171 - 20), left: (718 - 16) },
    path: [
      { origin: [-1, 1], direction: 'west' },
      { origin: [-3, 1], direction: 'west' },
      { origin: [-5, 1], direction: 'west' },
      { origin: [-5, 2], direction: 'south' },
      { origin: [-4, 3], direction: 'east' },
      { origin: [-2, 3], direction: 'east' },
    ] },

  { image: 'BG Level 4.png',
    origin: { top: (171 - 16), left: (208 - 16) },
    path: [
      { origin: [1, 0], direction: 'east' },
      { origin: [3, 0], direction: 'east' },
      { origin: [4, 1], direction: 'south' },
      { origin: [2, 2], direction: 'west' },
      { origin: [2, 3], direction: 'south' },
      { origin: [0, 4], direction: 'west' },
      { origin: [0, 5], direction: 'south' },
    ] },

  { image: 'BG Level 5.png',
    origin: { top: (171 - 16), left: (377 - 20) },
    path: [
      { origin: [1, 0], direction: 'east' },
      { origin: [2, 1], direction: 'south' },
      { origin: [0, 2], direction: 'west' },
      { origin: [-2, 2], direction: 'west' },
      { origin: [-2, 3], direction: 'south' },
      { origin: [-1, 4], direction: 'east' },
      { origin: [1, 4], direction: 'east' },
      { origin: [3, 4], direction: 'south' },
    ] },

  { image: 'BG Level 6.png',
    origin: { top: (171 - 16), left: (123 - 20) },
    path: [
      { origin: [1, 0], direction: 'east' },
      { origin: [2, 1], direction: 'south' },
      { origin: [3, 2], direction: 'east' },
      { origin: [5, 2], direction: 'east' },
      { origin: [6, 3], direction: 'south' },
      { origin: [4, 4], direction: 'west' },
      { origin: [2, 4], direction: 'west' },
      { origin: [2, 5], direction: 'south' },
      { origin: [0, 6], direction: 'west' },
    ] },

  { image: 'BG Level 7.png',
    origin: { top: (170 - 20), left: (298 - 16) },
    path: [
      { origin: [-1, 1], direction: 'west' },
      { origin: [-1, 2], direction: 'south' },
      { origin: [0, 3], direction: 'east' },
      { origin: [2, 3], direction: 'east' },
      { origin: [3, 4], direction: 'south' },
      { origin: [1, 5], direction: 'west' },
      { origin: [-1, 5], direction: 'west' },
      { origin: [-1, 6], direction: 'south' },
      { origin: [0, 7], direction: 'east' },
      { origin: [2, 7], direction: 'east' },
    ] },

  { image: 'BG Level 8.png',
    origin: { top: (171 - 16), left: (359 - 16) },
    path: [
      { origin: [0, 1], direction: 'south' },
      { origin: [0, 3], direction: 'south' },
      { origin: [-2, 4], direction: 'west' },
      { origin: [-2, 5], direction: 'south' },
      { origin: [-1, 6], direction: 'east' },
      { origin: [1, 6], direction: 'east' },
      { origin: [3, 6], direction: 'east' },
      { origin: [4, 7], direction: 'south' },
      { origin: [3, 9], direction: 'west' },
      { origin: [1, 9], direction: 'west' },
      { origin: [-1, 9], direction: 'west' },
    ] },
];

const rotationForDirection = (direction) => {
  if (direction === 'south') { return 'rotate-90'; }
  if (direction === 'west') { return 'rotate-180'; }
  if (direction === 'north') { return '-rotate-90'; }
  return 'rotate-0';
};

const translationForDirection = (direction) => {
  if (direction === 'south') { return '-translate-x-14 translate-y-14'; }
  return 'translate-0';
};

const renderStartingImage = (level, imgSrc, verticalOffset, horizontalOffset) => (
  <img
    className="m-4 rounded shadow-omni"
    style={{ position: 'absolute', top: level.origin.top + verticalOffset, left: level.origin.left + horizontalOffset, height: 104, width: 104 }}
    src={imgSrc}
    alt="First tile"
  />
);

const verticalFocus = (level, nPlacedTiles) => {
  const pathNode = level.path[nPlacedTiles];
  if (pathNode) {
    return level.origin.top + (pathNode.origin[1] * 112);
  }
  return 0;
};

const focusShiftX = (pathNode) => {
  if (pathNode.direction === 'west') {
    return 112 * 1.5;
  }
  return 0;
};

const horizontalFocus = (level, nPlacedTiles) => {
  const pathNode = level.path[nPlacedTiles];
  if (pathNode) {
    return level.origin.left + (pathNode.origin[0] * 112) + focusShiftX(pathNode);
  }
  return 0;
};

const useLastIndex = (placedTiles, firstImage) => {
  const [lastIndex, setLastIndex] = useState(firstImage);
  useEffect(() => {
    setLastIndex(placedTiles.length > 0 ? last(placedTiles)[1] : firstImage);
  }, [placedTiles, firstImage]);
  return lastIndex;
};

const useFocus = (level, placedTiles) => {
  const [focus, setFocus] = useState({ x: 0, y: 0 });
  useEffect(() => {
    if (level < levels.length) {
      const vOffset = verticalFocus(levels[level], placedTiles.length);
      const hOffset = horizontalFocus(levels[level], placedTiles.length);
      setFocus({ x: hOffset, y: vOffset });
    }
  }, [level, placedTiles]);
  return focus;
};

const calculateShift = (x) => {
  if (x <= 812) {
    return x * 0.25;
  }
  return x * 0.15;
};

const Domino = (props: Props) => {
  // Audio players
  const [playRight, setPlayRight] = useState(null);
  const [playWrong, setPlayWrong] = useState(null);
  const [playNextLevel, setPlayNextLevel] = useState(null);

  const audioGoed = props.audio.goed;
  const audioFout = props.audio.fout;
  const audioMoeilijker = props.audio.moeilijker;

  // Load audio assets
  const loadAssets = useCallback(() => [
    audioPlaylist(audioGoed).then(
      audioPlayer => setPlayRight(() => audioPlayer),
    ),
    audioPlaylist(audioFout).then(audioPlayer => setPlayWrong(() => audioPlayer)),
    audioPlaylist(audioMoeilijker).then(audioPlayer =>
      setPlayNextLevel(() => audioPlayer),
    ),
  ], [audioGoed, audioFout, audioMoeilijker]);

  const [gameState, setGameDone] = useGameState(loadAssets);
  const tileImageIndices = range(props.tileImages.length);
  const [tiles, setTiles] = useState([]);

  const windowSize = useWindowSize();

  const [placedTiles, setPlacedTiles] = useState([]);

  const [firstImage] = useState(sample(tileImageIndices));

  const lastIndex = useLastIndex(placedTiles, firstImage);

  useEffect(() => {
    if (tiles.length < 4) {
      setTiles([...tiles, newTile(tileImageIndices, tiles, lastIndex)]);
    }
  }, [tiles, tileImageIndices, placedTiles, firstImage, lastIndex]);

  const [level, setLevel] = useState(0);
  const [reversedTiles, setReversedTiles] = useState([]);

  const focus = useFocus(level, placedTiles);

  const [offset, setOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const halfWindowWidth = windowSize.width * 0.5;
    const halfWindowHeight = windowSize.height * 0.5;
    // Always shift the offset
    // Assuming that the tiles are shown at the right (or bottom) of screen:
    // Shift gives a slight offset towards the top left of the screen
    setOffset({ x: (focus.x - halfWindowWidth) + calculateShift(windowSize.width),
      y: (focus.y - halfWindowHeight) + (calculateShift(windowSize.height) * 0.75) });
  }, [windowSize, focus]);

  /* eslint-disable */
  const checkMatch = (tile, placedTiles, tiles, point) => {
    const matchingIndex = indexOf(tile, lastIndex);
    /* eslint-enable */

    // Since we shift the background, the position of the next tile is *always* near the center of
    // the window. So we only need to check if drop point is close to the center
    const horizontalMatch = Math.abs(point.x - (windowSize.width * 0.38)) < windowSize.width / 3;
    const verticalMatch = Math.abs(point.y - (windowSize.height * 0.38)) < windowSize.height / 3;

    if (matchingIndex >= 0 && horizontalMatch && verticalMatch) {
      if (placedTiles.length === levels[level].path.length - 1) {
        setPlacedTiles([]);
        setReversedTiles([]);
        setTiles([]);
        setLevel(level + 1);
        setOffset({ x: 0, y: 0 });
        if (level >= 7) {
          setGameDone();
          playRight();
        } else {
          playNextLevel();
        }
      } else {
        if (matchingIndex === 1) {
          setReversedTiles([...reversedTiles, placedTiles.length]);
        }
        setPlacedTiles([...placedTiles, matchingIndex === 0 ? tile : reverse(tile)]);
        setTiles(without(tiles, tile));
        playRight();
      }
    } else {
      playWrong();
    }
  };

  const absolutePositionOfTile = (thisLevel, pathNode) => (
    {
      top: thisLevel.origin.top + (pathNode.origin[1] * 114) + (offset.y * -1),
      left: thisLevel.origin.left + (pathNode.origin[0] * 114) + (offset.x * -1),
    }
  );

  const renderEmptyTile = () => {
    const thisLevel = levels[level];
    const pathNode = thisLevel.path[placedTiles.length];
    const direction = pathNode.direction;

    return (
      <div
        className={`absolute m-4 transform ${rotationForDirection(direction)} ${translationForDirection(direction)}`}
        style={absolutePositionOfTile(thisLevel, pathNode)}
      >
        <EmptyTile />
      </div>
    );
  };

  const renderLevel = () => (
    <AnimatePresence initial={true}>
      <motion.div
        key={level}
        initial={{ opacity: 0, x: 500, scale: 0.2 }}
        animate={{ opacity: 1, x: 0, scale: 1, transition: { duration: 1 } }}
        exit={{ opacity: 0, x: -500, scale: 0.2, transition: { duration: 0.5 } }}
        transition={{ ease: 'easeInOut', opacity: { duration: 0.5 } }}
        className="bg-no-repeat bg-left-top w-full h-screen relative z-20"
        style={{
          backgroundImage: `url("${props.assetPath}assets/${levels[level].image}")`,
          backgroundPosition: `${offset.x * -1}px ${offset.y * -1}px`,
          overflow: 'hidden',
          backgroundSize: '961px',
        }}
      >
        {
          renderStartingImage(
            levels[level],
            props.tileImages[firstImage],
            offset.y * -1,
            offset.x * -1,
          )
        }
        {
          placedTiles.map((tile, idx) => {
            const thisLevel = levels[level];
            const pathNode = thisLevel.path[idx];
            const direction = pathNode.direction;
            return (
              <div
                key={`placed-tile-${idx}`}
                className={`absolute m-4 transform ${rotationForDirection(direction)} ${translationForDirection(direction)}`}
                style={absolutePositionOfTile(thisLevel, pathNode)}
              >
                <Tile
                  tile={tile}
                  tileImages={props.tileImages}
                  currentRotation={thisLevel.path[placedTiles.length].direction}
                  reversed={includes(reversedTiles, idx)}
                  responsive={false}
                />
              </div>
            );
          })
        }
        {
          renderEmptyTile()
        }
      </motion.div>
    </AnimatePresence>
  );

  return (
    <Game
      gameState={gameState}
      relatedGames={[]}
      assetPath={'/'}
      isApp={false}
    >
      {level < levels.length && <>
        {
          renderLevel()
        }
        <Tiles
          tiles={tiles}
          tileImages={props.tileImages}
          checkMatch={checkMatch}
          placedTiles={placedTiles}
          currentRotation={levels[level].path[placedTiles.length].direction}
          lastIndex={lastIndex}
        />
      </>}
    </Game>
  );
};

const data = document.querySelector('#domino').dataset;
const props = {
  assetPath: data.assetPath,
  tileImages: JSON.parse(data.tiles),
  audio: JSON.parse(data.audio),
};
ReactDOM.render(<Domino {...props} />, document.querySelector('#domino'));
